import router from "@/router/index.js";
import axiosInstance from "@/plugins/axios";

export const actions = {
    setAuthenticated(authenticated) {
        this.authenticated = authenticated;
    },
    setUser(user) {
        this.user = user;
    },
    setToken(token) {
        this.jwtToken = token;
        if (token) {
            axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
        } else {
            delete axiosInstance.defaults.headers.common.Authorization;
        }
    },
    async refreshUser() {
        try {
            const response = await axiosInstance.get('/api/auth/user');
            this.setAuthenticated(true);
            this.setUser(response.data);
        } catch (error) {
            this.setAuthenticated(false);
            this.setUser(null);
        }
    },
    /** The account as saved on the server, for a session restored from this browser. */
    async syncUser() {
        const response = await axiosInstance.get('/api/auth/user').catch(() => null);
        if (response) this.setUser(response.data);
    },
    /**
     * Signs in and loads the account. Never throws:
     * { ok: true } or { ok: false, reason: 'credentials' | 'invalid' | 'locked' | 'network' | 'server', retryAfter? }.
     * Where to go next is the page's call (it knows the ?redirect).
     */
    async login({ email, password }) {
        try {
            const response = await axiosInstance.post('/api/login', { email: email.trim(), password });
            this.setToken(response.data.access_token);
            const user = await axiosInstance.get('/api/auth/user');
            this.setUser(user.data);
            this.setAuthenticated(true);
            return { ok: true };
        } catch (error) {
            this.clearSession();
            const status = error.response?.status;
            if (!error.response) return { ok: false, reason: 'network' };
            if (status === 401) return { ok: false, reason: 'credentials' };
            if (status === 422) return { ok: false, reason: 'invalid' };
            if (status === 429) {
                const retryAfter = Number(error.response.data?.retry_after ?? error.response.headers?.['retry-after'] ?? 60);
                return { ok: false, reason: 'locked', retryAfter };
            }
            return { ok: false, reason: 'server' };
        }
    },
    /** An Academy account; signed in straight away. Throws the request's error for the form to show. */
    async register(account) {
        const response = await axiosInstance.post('/api/register', { ...account, email: account.email.trim() });
        this.setToken(response.data.access_token);
        const user = await axiosInstance.get('/api/auth/user');
        this.setUser(user.data);
        this.setAuthenticated(true);
    },
    clearSession() {
        this.user = null;
        this.authenticated = false;
        this.setToken(null);
    },
    /**
     * Ends the session here and, unless the server already refused the token,
     * there too (the token is blacklisted, so a copy of it stops working).
     */
    async logout({ remote = true, redirect = null } = {}) {
        if (remote && this.jwtToken) {
            await axiosInstance.post('/api/auth/logout').catch(() => {});
        }
        const signIn = this.isTeam ? 'admin.login' : 'academy.signin';
        this.clearSession();
        router.push({ name: signIn, query: redirect ? { redirect } : {} });
    },
    async updateProfile(profile) {
        const response = await axiosInstance.put('/api/auth/profile', profile);
        this.setUser({ data: { ...this.user?.data, ...response.data.data } });
        return response.data.data;
    },
    async uploadProfilePhoto(file) {
        const formData = new FormData();
        formData.append('profile_photo', file);
        const response = await axiosInstance.post('/api/auth/profile/photo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.setUser({ data: { ...this.user?.data, ...response.data.data } });
    },
    async removeProfilePhoto() {
        const response = await axiosInstance.delete('/api/auth/profile/photo');
        this.setUser({ data: { ...this.user?.data, ...response.data.data } });
    },
    /** Every other session ends; this one carries on with the token the server hands back. */
    async changePassword(passwords) {
        const response = await axiosInstance.put('/api/auth/password', passwords);
        this.setToken(response.data.access_token);
    },
    async forgotPassword(email) {
        const response = await axiosInstance.post('/api/password/forgot', { email: email.trim() });
        return response.data.message;
    },
    async resetPassword(payload) {
        const response = await axiosInstance.post('/api/password/reset', payload);
        return response.data.message;
    },
};
